import type { PickerShellBucket } from '../PickerShell/PickerShellMolecule';
import type { TemplateApproach } from '../../../types/templateApproach';

export interface GroupApproachesOptions {
  /** `system_name` of the sphere's primary Archetype; its suggestions are listed first. */
  primary: string | null;
  suggestedForLabel: (archetypeSystemName: string) => string;
  otherLabel: string;
}

/**
 * Buckets approaches as "Suggested for {Archetype}" (primary first, then the others in the order
 * they first appear) followed by "Other approaches". An approach suggested by several Archetypes
 * sits under the earliest of them. With no suggestion at all the result is a single bucket with an
 * empty label — the caller renders it as a flat list. Shaped as `PickerShellBucket`s so it can feed
 * `PickerShellMolecule`'s `computeBuckets` unchanged.
 */
export function groupApproachesBySuggestion(
  approaches: TemplateApproach[],
  { primary, suggestedForLabel, otherLabel }: GroupApproachesOptions
): PickerShellBucket<TemplateApproach>[] {
  const order: string[] = primary ? [primary] : [];
  for (const approach of approaches) {
    for (const name of approach.suggestedBy ?? []) {
      if (!order.includes(name)) order.push(name);
    }
  }

  const byArchetype = new Map<string, TemplateApproach[]>();
  const others: TemplateApproach[] = [];
  for (const approach of approaches) {
    const home = order.find((name) => approach.suggestedBy?.includes(name));
    if (!home) {
      others.push(approach);
      continue;
    }
    byArchetype.set(home, [...(byArchetype.get(home) ?? []), approach]);
  }

  if (byArchetype.size === 0) return [{ key: 'other', label: '', items: others }];

  const buckets: PickerShellBucket<TemplateApproach>[] = order
    .filter((name) => byArchetype.has(name))
    .map((name) => ({
      key: `suggested:${name}`,
      label: suggestedForLabel(name),
      items: byArchetype.get(name) ?? [],
    }));
  if (others.length > 0) buckets.push({ key: 'other', label: otherLabel, items: others });
  return buckets;
}
